import React, { useEffect, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faXmark, faTrash, faCalendarDay, faGripLines, faFolderPlus, faPlus, faInbox, faCheck, faGear, faLock, faLockOpen,
    faDownload, faFileExport, faFileImport,
} from "@fortawesome/free-solid-svg-icons";
import { ReactSortable } from "react-sortablejs";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import "./FolderMenu.css";
import { AddFolder } from "./add-folder/AddFolder";
import { FolderMenuItem } from "./folder-menu-item/FolderMenuItem";
import { FolderHeader } from "./folder-header/FolderHeader";
import { AddListModal } from "./add-list-modal/AddListModal";
import { RenameFolderModal } from "./rename-folder-modal/RenameFolderModal";
import { ConfirmationDialog } from "../confirmation-dialog/ConfirmationDialog";
import { LanguageSwitcher } from "../language-switcher/LanguageSwitcher";
import { TrashBin } from "../trash-bin/TrashBin";
import { loadFromStorage, STORAGE_KEYS } from '../../utils/storageUtils';
import { createBackup, downloadBackup, parseBackup } from '../../utils/backupUtils';

export const FolderMenu = ({
    folders,
    setFolders,
    setShowListMenu,
    selectedCollectionId,
    onAddFolder,
    onSelectCollection,
    onMoveCollectionToFolder,
    onDeleteFolder,
    onRestoreElement,
    onRestoreCollection,
    onRestoreFolder,
    areSecretCollectionsVisible,
    onRequestSecretCollectionsVisibility,
    onRequestSetPin,
    onRequestChangePin,
    onRequestDeletePin,
    onRequestSecretCollectionCreation,
    onRestoreBackup,
    isInstallAvailable,
    onInstallApp,
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const isPlannerRoute = location.pathname === '/planner';
    const isReservedFolderName = (name) => name.trim().toLowerCase() === 'general';
    const isReservedCollectionName = (name) => name.trim().toLowerCase() === 'inbox';
    const [showTrashBin, setShowTrashBin] = useState(false);
    const [showReorderHandles, setShowReorderHandles] = useState(false);
    const [showNewMenu, setShowNewMenu] = useState(false);
    const [showToolsMenu, setShowToolsMenu] = useState(false);
    const newMenuRef = useRef(null);
    const toolsMenuRef = useRef(null);
    const backupInputRef = useRef(null);
    const [backupToImport, setBackupToImport] = useState(null);
    const [backupNotice, setBackupNotice] = useState(null);
    const [shouldBackupBeforeImport, setShouldBackupBeforeImport] = useState(true);

    const getBackupData = () => ({
        folders,
        selectedCollectionId: selectedCollectionId || null,
        trash: loadFromStorage(STORAGE_KEYS.TRASH, []) || [],
        secretCollectionPin: loadFromStorage(STORAGE_KEYS.SECRET_COLLECTION_PIN),
    });

    const handleExportBackup = (prefix = 'backup', showNotice = true) => {
        const filename = downloadBackup(createBackup(getBackupData()), prefix);
        if (showNotice) {
            setBackupNotice({
                title: t('backup.exportCompleteTitle'),
                message: t('backup.exportComplete', { filename }),
            });
        }

        return filename;
    };

    const handleImportFile = async (event) => {
        const [file] = event.target.files || [];
        event.target.value = '';
        if (!file) return;

        const backup = parseBackup(await file.text());
        if (!backup) {
            window.alert(t('backup.invalidFile'));
            return;
        }

        setBackupToImport(backup);
        setShouldBackupBeforeImport(true);
    };

    const confirmImportBackup = () => {
        const filename = shouldBackupBeforeImport
            ? handleExportBackup('backup-before-import', false)
            : null;
        onRestoreBackup(backupToImport);
        setBackupToImport(null);
        setShowToolsMenu(false);
        setBackupNotice({
            title: t('backup.importCompleteTitle'),
            message: filename
                ? t('backup.importComplete', { filename })
                : t('backup.importCompleteWithoutSafetyBackup'),
        });
    };
    const handleSelectCollection = (collectionId) => {
        onSelectCollection(collectionId);
        setShowListMenu(false);
        navigate('/');
    };

    const [searchQuery, setSearchQuery] = useState("");
    const [folderToDelete, setFolderToDelete] = useState(null);
    const [showAddListModal, setShowAddListModal] = useState(null); // folderId or null
    const [showAddFolderModal, setShowAddFolderModal] = useState(false);
    const [folderToRename, setFolderToRename] = useState(null); // folder object or null

    const normalizedQuery = searchQuery.trim().toLowerCase();

    useEffect(() => {
        if (!showNewMenu) return;

        const handlePointerDown = (event) => {
            if (newMenuRef.current && !newMenuRef.current.contains(event.target)) {
                setShowNewMenu(false);
            }
        };

        document.addEventListener('mousedown', handlePointerDown);
        document.addEventListener('touchstart', handlePointerDown);

        return () => {
            document.removeEventListener('mousedown', handlePointerDown);
            document.removeEventListener('touchstart', handlePointerDown);
        };
    }, [showNewMenu]);

    useEffect(() => {
        if (!showToolsMenu) return;

        const handlePointerDown = (event) => {
            if (toolsMenuRef.current && !toolsMenuRef.current.contains(event.target)) {
                setShowToolsMenu(false);
            }
        };

        document.addEventListener('mousedown', handlePointerDown);
        document.addEventListener('touchstart', handlePointerDown);

        return () => {
            document.removeEventListener('mousedown', handlePointerDown);
            document.removeEventListener('touchstart', handlePointerDown);
        };
    }, [showToolsMenu]);

    const folderCollectionsById = useMemo(() => {
        const map = new Map();
        for (const folder of folders || []) {
            const folderCollections = folder.collections || [];
            map.set(folder.id, folderCollections);
        }
        return map;
    }, [folders]);

    const inboxCollection = useMemo(() => {
        for (const folder of folders || []) {
            const collection = (folder.collections || []).find((c) => c.name === 'Inbox');
            if (collection) return collection;
        }
        return null;
    }, [folders]);

    const defaultFolder = useMemo(
        () => (folders || []).find((folder) => folder.name === 'General'),
        [folders],
    );

    const visibleFolders = useMemo(() => {
        const nonDefaultFolders = (folders || []).filter((folder) => folder.id !== defaultFolder?.id);
        if (!normalizedQuery) return nonDefaultFolders;
        return nonDefaultFolders.filter((folder) => {
            const folderCollections = folderCollectionsById.get(folder.id) || [];
            return folderCollections.some((collection) =>
                !collection.secret && (collection.name || "").toLowerCase().includes(normalizedQuery),
            );
        });
    }, [folders, normalizedQuery, folderCollectionsById, defaultFolder?.id]);

    const mergeReorderedCollections = (currentCollections = [], newCollectionOrder = []) => {
        const reorderedCollections = [...newCollectionOrder];
        const reorderedIds = new Set(reorderedCollections.map((collection) => collection.id));
        const mergedCollections = currentCollections.map((collection) => {
            if (!reorderedIds.has(collection.id)) return collection;
            return reorderedCollections.shift() || collection;
        });

        return [...mergedCollections, ...reorderedCollections];
    };

    const setFolderOrder = (folderId, newCollectionOrder) => {
        setFolders((prevFolders) =>
            prevFolders.map((folder) => {
                if (folder.id !== folderId) return folder;

                return {
                    ...folder,
                    collections: mergeReorderedCollections(folder.collections || [], newCollectionOrder),
                };
            }),
        );
    };

    const setFoldersOrder = (newFolderOrder) => {
        setFolders((prevFolders) => {
            const reorderedFolders = [...newFolderOrder];
            const reorderedFolderIds = new Set(reorderedFolders.map((folder) => folder.id));

            return prevFolders.map((folder) => {
                if (!reorderedFolderIds.has(folder.id)) {
                    return folder;
                }

                return reorderedFolders.shift() || folder;
            });
        });
    };

    const toggleFolderCollapse = (folderId) => {
        setFolders((prevFolders) =>
            prevFolders.map((folder) => {
                if (folder.id === folderId) {
                    return { ...folder, collapsed: !folder.collapsed };
                }

                if (!normalizedQuery && !showReorderHandles) {
                    return { ...folder, collapsed: true };
                }

                return folder;
            }),
        );
    };

    const handleDeleteFolder = (folderId) => {
        setFolderToDelete(folderId);
    };

    const confirmDeleteFolder = () => {
        if (folderToDelete) {
            onDeleteFolder(folderToDelete);
            setFolderToDelete(null);
        }
    };

    const cancelDeleteFolder = () => {
        setFolderToDelete(null);
    };

    const handleAddCollectionToFolder = (folderId) => {
        setShowAddListModal(folderId);
    };

    const handleAddCollectionToDefaultFolder = () => {
        const targetFolder = defaultFolder || (folders || [])[0];
        if (targetFolder) {
            setShowAddListModal(targetFolder.id);
        }
        setShowNewMenu(false);
    };

    const createCollection = (folderId, collectionName, isSecret = false) => {
        const newCollection = {
            id: Date.now().toString(),
            name: collectionName.trim(),
            elements: [],
            secret: isSecret,
            visibleTypes: ['task'],
        };
        setFolders((prevFolders) => prevFolders.map((folder) =>
            folder.id === folderId
                ? { ...folder, collections: [...(folder.collections || []), newCollection] }
                : folder,
        ));
        onSelectCollection(newCollection.id);
        setShowAddListModal(null);
        setShowListMenu(false);
        navigate('/');
    };

    const handleCreateCollectionInFolder = (collectionName, isSecret = false) => {
        if (showAddListModal && collectionName.trim()) {
            if (isReservedCollectionName(collectionName)) {
                window.alert(t('folderMenu.reservedCollectionName'));
                return;
            }

            if (isSecret) {
                setShowAddListModal(null);
                onRequestSecretCollectionCreation(() => {
                    createCollection(showAddListModal, collectionName, true);
                });
                return;
            }
            createCollection(showAddListModal, collectionName, isSecret);
        }
    };

    const handleSecretCollectionsVisibility = () => {
        onRequestSecretCollectionsVisibility();
    };

    const handleSetPin = () => {
        onRequestSetPin();
        setShowToolsMenu(false);
    };

    const handleChangePin = () => {
        onRequestChangePin();
        setShowToolsMenu(false);
    };

    const handleDeletePin = () => {
        onRequestDeletePin();
        setShowToolsMenu(false);
    };

    const handleCancelAddList = () => {
        setShowAddListModal(null);
    };

    const handleCreateFolder = (folderName) => {
        if (isReservedFolderName(folderName)) {
            window.alert(t('folderMenu.reservedFolderName'));
            return;
        }

        onAddFolder(folderName);
        setShowNewMenu(false);
        setShowAddFolderModal(false);
    };

    const handleRenameFolder = (folder) => {
        setFolderToRename(folder);
    };

    const handleRenameFolderSubmit = (newName) => {
        if (folderToRename) {
            if (isReservedFolderName(newName)) {
                window.alert(t('folderMenu.reservedFolderName'));
                return;
            }

            setFolders((prevFolders) =>
                prevFolders.map((folder) =>
                    folder.id === folderToRename.id
                        ? { ...folder, name: newName }
                        : folder,
                ),
            );
            setFolderToRename(null);
        }
    };

    const handleCancelRenameFolder = () => {
        setFolderToRename(null);
    };

    const handleNavigateToPlanner = () => {
        navigate('/planner');
        setShowListMenu(false);
    };

    const defaultFolderCollections = defaultFolder
        ? folderCollectionsById.get(defaultFolder.id) || []
        : [];
    const shouldHideInboxShortcutCollection = Boolean(
        inboxCollection &&
        !normalizedQuery
    );
    const displayRootCollections = shouldHideInboxShortcutCollection
        ? defaultFolderCollections.filter((collection) =>
            collection.id !== inboxCollection.id && (!collection.secret || areSecretCollectionsVisible),
        )
        : defaultFolderCollections.filter((collection) => !collection.secret || areSecretCollectionsVisible);
    const visibleRootCollections = normalizedQuery
        ? defaultFolderCollections.filter((collection) =>
            !collection.secret && (collection.name || "").toLowerCase().includes(normalizedQuery),
        )
        : displayRootCollections;

    return (
        <div className="folder-menu">
            <div className="folder-menu-header">
                <LanguageSwitcher />
                <div className="header-actions">
                    <button
                        type="button"
                        className="secret-collections-toggle"
                        onClick={handleSecretCollectionsVisibility}
                        aria-label={t(
                            areSecretCollectionsVisible ? 'secretCollections.hide' : 'secretCollections.show',
                        )}
                        title={t(areSecretCollectionsVisible ? 'secretCollections.hide' : 'secretCollections.show')}
                    >
                        <FontAwesomeIcon icon={areSecretCollectionsVisible ? faLockOpen : faLock} />
                    </button>
                    <div className="close-menu-icon" onClick={() => setShowListMenu(false)}>
                        <FontAwesomeIcon icon={faXmark} />
                    </div>
                </div>
            </div>
            <div className="folder-menu-container">
                <AddFolder
                    searchQuery={searchQuery}
                    onSearchQueryChange={setSearchQuery}
                />
                <div className={`planner-menu-item ${isPlannerRoute ? 'selected' : ''}`} onClick={handleNavigateToPlanner}>
                    <FontAwesomeIcon icon={faCalendarDay} className="planner-menu-icon" />
                    <span className="planner-menu-text">{t('taskListHeader.plannerView')}</span>
                </div>
                {inboxCollection && (
                    <div
                        className={`planner-menu-item ${!isPlannerRoute && selectedCollectionId === inboxCollection.id ? 'selected' : ''}`}
                        onClick={() => handleSelectCollection(inboxCollection.id)}
                    >
                        <FontAwesomeIcon icon={faInbox} className="planner-menu-icon" />
                        <span className="planner-menu-text">
                            {t('folderMenu.inbox')}
                            <span className="planner-menu-count">
                                ({inboxCollection?.elements?.length || inboxCollection?.todos?.length || 0})
                            </span>
                        </span>
                    </div>
                )}
                <div className="menu-separator"></div>
                {defaultFolder && visibleRootCollections.length > 0 && (
                    <div className="folder-menu-section">
                        <div className="folder-menu-section-title">{t('folderMenu.lists')}</div>
                        <div className="root-list-items">
                            <ReactSortable
                                list={visibleRootCollections}
                                setList={(newSubset) => setFolderOrder(defaultFolder.id, newSubset)}
                                handle=".list-drag-handle"
                                disabled={!showReorderHandles}
                            >
                                {visibleRootCollections.map((collection) => (
                                    <FolderMenuItem
                                        key={collection.id}
                                        list={collection}
                                        folders={folders}
                                        selectedListId={isPlannerRoute ? undefined : selectedCollectionId}
                                        onSelectList={handleSelectCollection}
                                        onMoveListToFolder={onMoveCollectionToFolder}
                                        showReorderHandle={showReorderHandles}
                                    />
                                ))}
                            </ReactSortable>
                        </div>
                    </div>
                )}
                {visibleFolders.length > 0 && (
                <div className="folder-menu-section">
                    <div className="folder-menu-section-title">{t('folderMenu.groups')}</div>
                    <ReactSortable
                        list={visibleFolders}
                        setList={setFoldersOrder}
                        handle=".folder-drag-handle"
                        disabled={!showReorderHandles}
                    >
                        {visibleFolders.map((folder) => {
                            const folderCollections = folderCollectionsById.get(folder.id) || [];
                            const displayFolderCollections = (shouldHideInboxShortcutCollection
                                ? folderCollections.filter((c) => c.id !== inboxCollection.id)
                                : folderCollections).filter((collection) =>
                                !collection.secret || areSecretCollectionsVisible,
                            );
                            const visibleFolderCollections = normalizedQuery
                                ? folderCollections.filter((collection) =>
                                    !collection.secret && (collection.name || "")
                                        .toLowerCase()
                                        .includes(normalizedQuery),
                                )
                                : displayFolderCollections;

                            return (
                                <div key={folder.id}>
                                    <FolderHeader
                                        folder={folder}
                                        listCount={visibleFolderCollections.length}
                                        selected={Boolean(
                                            folder.collapsed &&
                                            !isPlannerRoute &&
                                            folderCollections.some((collection) => collection.id === selectedCollectionId)
                                        )}
                                        onAddList={() => handleAddCollectionToFolder(folder.id)}
                                        onDeleteFolder={() => handleDeleteFolder(folder.id)}
                                        onToggleCollapse={() => toggleFolderCollapse(folder.id)}
                                        onRenameFolder={() => handleRenameFolder(folder)}
                                        showReorderHandle={showReorderHandles}
                                        showAddListAction={!folder.collapsed && visibleFolderCollections.length > 0}
                                    />
                                    {!folder.collapsed && (
                                        <div className="folder-list-items">
                                            {visibleFolderCollections.length > 0 && (
                                                <ReactSortable
                                                    list={visibleFolderCollections}
                                                    setList={(newSubset) => setFolderOrder(folder.id, newSubset)}
                                                    handle=".list-drag-handle"
                                                    disabled={!showReorderHandles}
                                                >
                                                    {visibleFolderCollections.map((collection) => (
                                                        <FolderMenuItem
                                                            key={collection.id}
                                                            list={collection}
                                                            folders={folders}
                                                            selectedListId={isPlannerRoute ? undefined : selectedCollectionId}
                                                            onSelectList={handleSelectCollection}
                                                            onMoveListToFolder={onMoveCollectionToFolder}
                                                            showReorderHandle={showReorderHandles}
                                                        />
                                                    ))}
                                                </ReactSortable>
                                            )}
                                            {visibleFolderCollections.length === 0 && (
                                                <button
                                                    type="button"
                                                    className="folder-add-list-row"
                                                    onClick={() => handleAddCollectionToFolder(folder.id)}
                                                >
                                                    <FontAwesomeIcon icon={faPlus} />
                                                    <span>{t('folderMenu.addList')}</span>
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </ReactSortable>
                </div>
                )}
            </div>
            <div className="folder-menu-footer">
                <div className="folder-menu-new" ref={newMenuRef}>
                    {showNewMenu && (
                        <div className="folder-menu-popover">
                            <button
                                type="button"
                                className="folder-menu-popover-option"
                                onClick={handleAddCollectionToDefaultFolder}
                            >
                                <FontAwesomeIcon icon={faPlus} />
                                <span>{t('folderMenu.newList')}</span>
                            </button>
                            <button
                                type="button"
                                className="folder-menu-popover-option"
                                onClick={() => {
                                    setShowNewMenu(false);
                                    setShowAddFolderModal(true);
                                }}
                            >
                                <FontAwesomeIcon icon={faFolderPlus} />
                                <span>{t('folderMenu.newGroup')}</span>
                            </button>
                        </div>
                    )}
                    <button
                        type="button"
                        className="folder-menu-new-button"
                        onClick={() => {
                            setShowToolsMenu(false);
                            setShowNewMenu((isOpen) => !isOpen);
                        }}
                    >
                        <FontAwesomeIcon icon={faPlus} />
                        <span>{t('folderMenu.add')}</span>
                    </button>
                </div>
                <div className="folder-menu-tools" ref={toolsMenuRef}>
                    {showToolsMenu && (
                        <div className="folder-menu-popover folder-menu-tools-popover">
                            <button
                                type="button"
                                className="folder-menu-popover-option"
                                onClick={() => {
                                    setShowReorderHandles((isVisible) => !isVisible);
                                    setShowToolsMenu(false);
                                }}
                            >
                                <FontAwesomeIcon icon={showReorderHandles ? faCheck : faGripLines} />
                                <span>
                                    {showReorderHandles ? t('folderMenu.doneReorder') : t('folderMenu.reorder')}
                                </span>
                            </button>
                            <button
                                type="button"
                                className="folder-menu-popover-option"
                                onClick={() => handleExportBackup()}
                            >
                                <FontAwesomeIcon icon={faFileExport} />
                                <span>{t('backup.export')}</span>
                            </button>
                            <button
                                type="button"
                                className="folder-menu-popover-option"
                                onClick={() => backupInputRef.current?.click()}
                            >
                                <FontAwesomeIcon icon={faFileImport} />
                                <span>{t('backup.import')}</span>
                            </button>
                            {isInstallAvailable && (
                                <button
                                    type="button"
                                    className="folder-menu-popover-option"
                                    onClick={() => {
                                        onInstallApp();
                                        setShowToolsMenu(false);
                                    }}
                                >
                                    <FontAwesomeIcon icon={faDownload} />
                                    <span>{t('pwa.install')}</span>
                                </button>
                            )}
                            {loadFromStorage(STORAGE_KEYS.SECRET_COLLECTION_PIN) ? (
                                <>
                                    <button
                                        type="button"
                                        className="folder-menu-popover-option"
                                        onClick={handleChangePin}
                                    >
                                        <FontAwesomeIcon icon={faLockOpen} />
                                        <span>{t('secretCollections.changePin')}</span>
                                    </button>
                                    <button
                                        type="button"
                                        className="folder-menu-popover-option"
                                        onClick={handleDeletePin}
                                    >
                                        <FontAwesomeIcon icon={faLock} />
                                        <span>{t('secretCollections.deletePin')}</span>
                                    </button>
                                </>
                            ) : (
                                <button
                                    type="button"
                                    className="folder-menu-popover-option"
                                    onClick={handleSetPin}
                                >
                                    <FontAwesomeIcon icon={faLock} />
                                    <span>{t('secretCollections.setPin')}</span>
                                </button>
                            )}
                            <button
                                type="button"
                                className="folder-menu-popover-option"
                                onClick={() => {
                                    setShowTrashBin(true);
                                    setShowToolsMenu(false);
                                }}
                            >
                                <FontAwesomeIcon icon={faTrash} />
                                <span>{t('trashBin.title')}</span>
                            </button>
                        </div>
                    )}
                    <button
                        type="button"
                        className="folder-menu-tools-button"
                        onClick={() => {
                            setShowNewMenu(false);
                            setShowToolsMenu((isOpen) => !isOpen);
                        }}
                        aria-label={t('folderMenu.tools')}
                        title={t('folderMenu.tools')}
                    >
                        <FontAwesomeIcon icon={faGear} />
                    </button>
                </div>
            </div>
            {folderToDelete && (
                <ConfirmationDialog
                    title={t('folderMenu.deleteFolder')}
                    message={t('folderMenu.deleteFolderConfirm')}
                    onConfirm={confirmDeleteFolder}
                    onCancel={cancelDeleteFolder}
                    isDanger={true}
                />
            )}
            {showAddListModal && (
                <AddListModal
                    onAddList={handleCreateCollectionInFolder}
                    onCancel={handleCancelAddList}
                />
            )}
            {showAddFolderModal && (
                <AddListModal
                    title={t('folderMenu.addFolder')}
                    placeholder={t('folderMenu.addGroupName')}
                    onAddList={handleCreateFolder}
                    onCancel={() => setShowAddFolderModal(false)}
                />
            )}
            {folderToRename && (
                <RenameFolderModal
                    folderName={folderToRename.name}
                    onRename={handleRenameFolderSubmit}
                    onCancel={handleCancelRenameFolder}
                />
            )}
            {showTrashBin && (
                <TrashBin
                    isOpen={showTrashBin}
                    onClose={() => setShowTrashBin(false)}
                    onRestoreElement={onRestoreElement}
                    onRestoreCollection={onRestoreCollection}
                    onRestoreFolder={onRestoreFolder}
                    folders={folders}
                    areSecretCollectionsVisible={areSecretCollectionsVisible}
                    isSecretProtectionEnabled={Boolean(loadFromStorage(STORAGE_KEYS.SECRET_COLLECTION_PIN))}
                />
            )}
            <input
                ref={backupInputRef}
                type="file"
                accept="application/json,.json"
                hidden
                onChange={handleImportFile}
            />
            {backupToImport && (
                <div className="backup-notice-overlay" onClick={() => setBackupToImport(null)}>
                    <section
                        className="backup-notice-dialog backup-import-dialog"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="backup-import-title"
                        onClick={(event) => event.stopPropagation()}
                    >
                        <div className="backup-notice-header">
                            <h3 id="backup-import-title">{t('backup.importTitle')}</h3>
                            <button
                                type="button"
                                className="backup-notice-close"
                                onClick={() => setBackupToImport(null)}
                                aria-label={t('backup.closeNotice')}
                            >
                                <FontAwesomeIcon icon={faXmark} />
                            </button>
                        </div>
                        <p>{t('backup.importConfirm')}</p>
                        <label className="backup-import-safety-option">
                            <input
                                type="checkbox"
                                checked={shouldBackupBeforeImport}
                                onChange={(event) => setShouldBackupBeforeImport(event.target.checked)}
                            />
                            <span>{t('backup.createSafetyBackup')}</span>
                        </label>
                        {!shouldBackupBeforeImport && (
                            <p className="backup-import-warning">{t('backup.importWithoutSafetyWarning')}</p>
                        )}
                        <div className="backup-import-actions">
                            <button
                                type="button"
                                className="backup-import-cancel"
                                onClick={() => setBackupToImport(null)}
                            >
                                {t('confirmationDialog.cancel')}
                            </button>
                            <button
                                type="button"
                                className="backup-import-confirm"
                                onClick={confirmImportBackup}
                            >
                                {t('backup.importAndReplace')}
                            </button>
                        </div>
                    </section>
                </div>
            )}
            {backupNotice && (
                <div className="backup-notice-overlay" onClick={() => setBackupNotice(null)}>
                    <section
                        className="backup-notice-dialog"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="backup-notice-title"
                        onClick={(event) => event.stopPropagation()}
                    >
                        <div className="backup-notice-header">
                            <h3 id="backup-notice-title">{backupNotice.title}</h3>
                            <button
                                type="button"
                                className="backup-notice-close"
                                onClick={() => setBackupNotice(null)}
                                aria-label={t('backup.closeNotice')}
                            >
                                <FontAwesomeIcon icon={faXmark} />
                            </button>
                        </div>
                        <p>{backupNotice.message}</p>
                        <button
                            type="button"
                            className="backup-notice-confirm"
                            onClick={() => setBackupNotice(null)}
                        >
                            {t('backup.gotIt')}
                        </button>
                    </section>
                </div>
            )}
        </div>
    );
};
